import { getToken, main_url } from "../../../utilis/Common";

export const OCR_API_BASE = `${main_url().replace(/\/+$/, "")}/api/v1`;
const PDF_TIMEOUT_MS = 10 * 60 * 1000;
const BUSY_RETRY_DELAY_MS = 2500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {File} file
 * @param {number} timeoutMs
 * @returns {Promise<Response>}
 */
const postPdfFile = async (file, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const form = new FormData();
  form.append("file", file, file.name || "document.pdf");

  const headers = {};
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    return await fetch(`${OCR_API_BASE}/ocr/extract-text-pdf`, {
      method: "POST",
      body: form,
      headers,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
};

/**
 * @param {Response} response
 * @returns {Promise<{ success: boolean, page_count?: number, pages?: Array, error?: string }>}
 */
const parsePdfOcrResponse = async (response) => {
  let body;
  try {
    body = await response.json();
  } catch {
    throw new Error(`OCR service returned ${response.status} with an invalid response.`);
  }

  if (!response.ok) {
    const message =
      (body && typeof body.error === "string" && body.error) ||
      `OCR service returned ${response.status}.`;
    throw new Error(message);
  }

  if (!body || typeof body !== "object") {
    throw new Error("PDF OCR returned an invalid response.");
  }

  if (!body.success) {
    return body;
  }

  if (!Array.isArray(body.pages)) {
    throw new Error("PDF OCR returned an invalid response.");
  }

  for (const entry of body.pages) {
    if (typeof entry?.page !== "number" || entry.page < 1 || !Array.isArray(entry.lines)) {
      throw new Error("PDF OCR returned an invalid page structure.");
    }
  }

  return body;
};

/**
 * @param {File} file
 * @returns {Promise<{ success: boolean, page_count?: number, pages?: Array, error?: string }>}
 */
export async function extractPdfText(file) {
  let response;
  try {
    response = await postPdfFile(file, PDF_TIMEOUT_MS);
  } catch (err) {
    throw new Error(
      `Could not reach OCR service at ${OCR_API_BASE} (${
        err instanceof Error ? err.message : String(err)
      })`,
    );
  }

  if (response.status === 503) {
    await sleep(BUSY_RETRY_DELAY_MS);
    try {
      response = await postPdfFile(file, PDF_TIMEOUT_MS);
    } catch (err) {
      throw new Error(
        `Could not reach OCR service at ${OCR_API_BASE} (${
          err instanceof Error ? err.message : String(err)
        })`,
      );
    }
  }

  return parsePdfOcrResponse(response);
}
